import { Loading } from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaDownload, FaFileExcel, FaFilter, FaInfoCircle, FaListUl, FaSearch, FaTimes, FaUpload } from 'react-icons/fa'
import { useAuthUserData } from '../../../authUserProvider'
import api, { apiUpload } from '../../../helpers/apiRequester'
import {
    ClaimsTable,
    ClientResultList,
    ClientSearchForm,
    CustomValuesForm,
    EMAIL_REGEX,
    GranularityControl,
    TemplatePicker,
    clientKey,
    downloadText,
} from './shared'
import styles from './styles.module.scss'
import {
    ClaimRule,
    FIELD_TYPE_LABELS,
    Granularity,
    SelectedClient,
    TemplateField,
    TemplateRow,
    defaultGranularity,
    formatDate,
    missingForClient,
    pickClaimByRule,
    templateRequirements,
} from './types'

type SourceKind = 'sheet' | 'search' | 'filter' | 'identifiers'

const SOURCES: { kind: SourceKind; title: string; body: string; icon: JSX.Element }[] = [
    {
        kind: 'sheet',
        title: 'Spreadsheet',
        body: 'Upload a CSV or Excel file whose columns match the template. Every row becomes a document.',
        icon: <FaFileExcel />,
    },
    {
        kind: 'search',
        title: 'Client search',
        body: 'Find clients across every database and build a list, choosing which claims to include.',
        icon: <FaSearch />,
    },
    {
        kind: 'filter',
        title: 'Introducer filter',
        body: 'Pull a population from one or more introducers by lender, CFA state and date range.',
        icon: <FaFilter />,
    },
    {
        kind: 'identifiers',
        title: 'Identifier list',
        body: 'Paste or upload emails / case references and resolve them across introducers.',
        icon: <FaListUl />,
    },
]

interface BatchConfig {
    template: TemplateRow | null
    fields: TemplateField[]
    granularity: Granularity
    claimRule: ClaimRule
    customValues: Record<string, string>
    name: string
    sentBy: string
}

interface BatchItemInput {
    client_id: number
    origin: string
    case_ref: string | null
    recipient_name?: string | null
    recipient_email?: string | null
}

interface OriginOption {
    key: string
    label: string
    supports_filter: boolean
}

interface BulkSendTabProps {
    templates: TemplateRow[]
    templatesLoaded: boolean
    onBatchCreated: (batchId: number) => void
    onGoToVault: () => void
    onEditTemplate: (templateId: number) => void
}

const configProblems = (config: BatchConfig) => {
    const problems: string[] = []
    if (!config.template) problems.push('choose a template')
    const { customFields } = templateRequirements(config.fields)
    customFields.forEach((f) => {
        if (!(config.customValues[f.id] || '').trim()) problems.push(`fill in "${f.label}"`)
    })
    return problems
}

/** Creates a batch from DB-backed items and optionally starts it straight away. */
const createBatch = async (
    config: BatchConfig,
    source: SourceKind,
    items: BatchItemInput[],
    sourceMeta: Record<string, unknown> | null,
    start: boolean,
): Promise<number | null> => {
    if (!config.template) return null
    Loading.circle(start ? 'Creating and starting the bulk send...' : 'Saving the bulk send...')
    try {
        const created = await api({
            url: 'contract-sign/bulk/create',
            data: {
                template_id: config.template.id,
                name: config.name.trim(),
                source,
                granularity: config.granularity,
                claim_rule: config.granularity === 'client' ? config.claimRule : null,
                items,
                custom_values: config.customValues,
                source_meta: sourceMeta,
                sent_by: config.sentBy,
            },
        })
        if (!created.data?.success) {
            Loading.remove()
            Report.failure('Error', created.data?.message || 'Could not create the bulk send.', 'Okay')
            return null
        }
        const batchId: number = created.data.batch_id
        if (start) {
            const started = await api({ url: 'contract-sign/bulk/start', data: { batch_id: batchId } })
            Loading.remove()
            if (!started.data?.success) {
                Report.failure('Saved, not started', started.data?.message || 'Could not start the bulk send.', 'Okay')
                return batchId
            }
        } else {
            Loading.remove()
        }
        return batchId
    } catch (e: any) {
        Loading.remove()
        Report.failure('Error', e?.response?.data?.message || 'Could not create the bulk send.', 'Okay')
        return null
    }
}

const confirmSend = (count: number, esign: boolean, onConfirm: () => void) =>
    Confirm.show(
        'Start bulk send',
        `Send ${count} ${esign ? 'signing links' : 'documents'} now? They go out in the background and you can track them under Bulk sends.`,
        'Send now',
        'Cancel',
        onConfirm,
    )

/* Actions footer shared by the DB-backed sources ------------------------- */

interface SendActionsProps {
    config: BatchConfig
    count: number
    label: string
    disabledReason?: string | null
    onCreate: (start: boolean) => void
}

const SendActions = ({ config, count, label, disabledReason, onCreate }: SendActionsProps) => {
    const problems = configProblems(config)
    const blocked =
        disabledReason ||
        (problems.length ? `Please ${problems.join(', ')}.` : null) ||
        (count === 0 ? 'Nothing to send yet.' : null)
    const esign = templateRequirements(config.fields).hasSignature
    return (
        <div className={styles.stickyActions}>
            <span className={styles.stackCell}>
                <span className={styles.stackPrimary}>{label}</span>
                <span className={styles.stackSecondary}>
                    {blocked || `${count} ${esign ? 'signing links' : 'documents'} ready to send`}
                </span>
            </span>
            <span className={styles.rowActions}>
                <button
                    type="button"
                    className={styles.secondaryBtn}
                    disabled={Boolean(blocked)}
                    onClick={() => onCreate(false)}
                >
                    Save as draft
                </button>
                <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={Boolean(blocked)}
                    onClick={() => confirmSend(count, esign, () => onCreate(true))}
                >
                    Send {count > 0 ? count : ''} now
                </button>
            </span>
        </div>
    )
}

/* Origins picker --------------------------------------------------------- */

const useOrigins = () => {
    const [origins, setOrigins] = useState<OriginOption[]>([])
    useEffect(() => {
        let cancelled = false
        api({ url: 'contract-sign/bulk/origins', data: {} })
            .then((res) => {
                if (!cancelled && res.data?.success) setOrigins(res.data.origins || [])
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [])
    return origins
}

interface OriginsPickerProps {
    origins: OriginOption[]
    selected: Set<string>
    onChange: (next: Set<string>) => void
}

const OriginsPicker = ({ origins, selected, onChange }: OriginsPickerProps) => {
    const usable = origins.filter((o) => o.supports_filter)
    const toggle = (key: string) => {
        const next = new Set(selected)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        onChange(next)
    }
    return (
        <div className={styles.field}>
            <label className={styles.fieldLabel}>
                Data sources
                <span className={styles.fieldMeta}>
                    <button
                        type="button"
                        className={styles.compactBtn}
                        onClick={() => onChange(new Set(usable.map((o) => o.key)))}
                    >
                        all
                    </button>{' '}
                    <button type="button" className={styles.compactBtn} onClick={() => onChange(new Set())}>
                        none
                    </button>
                </span>
            </label>
            <div className={styles.checkboxRow}>
                {origins.length === 0 && <span className={styles.muted}>Loading data sources...</span>}
                {origins.map((o) => (
                    <label
                        key={o.key}
                        className={`${styles.checkboxLabel} ${o.supports_filter ? '' : styles.disabled}`}
                    >
                        <input
                            type="checkbox"
                            disabled={!o.supports_filter}
                            checked={selected.has(o.key)}
                            onChange={() => toggle(o.key)}
                        />
                        {o.label}
                        {!o.supports_filter && ' (search only)'}
                    </label>
                ))}
            </div>
        </div>
    )
}

/* Preview table for server-built items ----------------------------------- */

interface ServerItem extends BatchItemInput {
    lender?: string | null
    start_date?: string | null
    claim_count?: number
}

const ItemsPreview = ({ items, granularity }: { items: ServerItem[]; granularity: Granularity }) => {
    const shown = items.slice(0, 50)
    if (items.length === 0) return null
    return (
        <>
            <p className={styles.resultLine}>
                Showing {shown.length} of {items.length}{' '}
                {granularity === 'claim' ? 'documents (one per claim)' : 'clients'}
            </p>
            <div className={styles.tableWrap}>
                <table className={`${styles.table} ${styles.compactTable}`}>
                    <thead>
                        <tr>
                            <th>Recipient</th>
                            <th>Source</th>
                            <th>{granularity === 'claim' ? 'Claim' : 'Claims'}</th>
                            <th>Lender</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((item, i) => (
                            <tr
                                key={`${item.origin}-${item.client_id}-${item.case_ref || i}`}
                                className={styles.staticRow}
                            >
                                <td>
                                    <div className={styles.stackCell}>
                                        <span className={styles.stackPrimary}>{item.recipient_name || '—'}</span>
                                        <span
                                            className={`${styles.stackSecondary} ${item.recipient_email ? '' : styles.toneBad}`}
                                        >
                                            {item.recipient_email || 'no email — will fail'}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <span className={`${styles.pill} ${styles.muted}`}>
                                        {item.origin} · #{item.client_id}
                                    </span>
                                </td>
                                <td className={styles.nowrap}>
                                    {granularity === 'claim' ? item.case_ref : `${item.claim_count ?? '—'} on record`}
                                </td>
                                <td>{item.lender || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    )
}

/* Source: spreadsheet ---------------------------------------------------- */

interface ColumnSpec {
    key: string
    header: string
    required: boolean
    custom_id?: string
}

interface SheetUploadResult {
    batch_id: number
    total: number
    skipped: number
    granularity: Granularity
    delivery_type: string
    preview: { recipient_name: string | null; recipient_email: string | null; case_ref: string | null }[]
    skipped_rows: { row: number; email: string; name: string; reason: string }[]
    ignored_headers: string[]
}

const SheetSource = ({ config, onBatchCreated }: { config: BatchConfig; onBatchCreated: (id: number) => void }) => {
    const [columns, setColumns] = useState<ColumnSpec[] | null>(null)
    const [file, setFile] = useState<File | null>(null)
    const [result, setResult] = useState<SheetUploadResult | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        setColumns(null)
        setResult(null)
        if (!config.template) return
        let cancelled = false
        api({ url: 'contract-sign/templates/csv-headers', data: { template_id: config.template.id } })
            .then((res) => {
                if (!cancelled && res.data?.success) setColumns(res.data.columns || [])
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [config.template])

    const downloadTemplate = () => {
        if (!columns || !config.template) return
        const header = columns
            .map((c) => (/[",\n]/.test(c.header) ? `"${c.header.replace(/"/g, '""')}"` : c.header))
            .join(',')
        downloadText(
            `${config.template.name.replace(/[^a-z0-9-_ ]/gi, '').trim() || 'template'}-recipients.csv`,
            `${header}\n`,
        )
    }

    const upload = async () => {
        if (!config.template || !file) return
        const problems = configProblems(config)
        if (problems.length) return Report.failure('Missing details', `Please ${problems.join(', ')}.`, 'Okay')
        const form = new FormData()
        form.append('sheet', file)
        form.append('template_id', String(config.template.id))
        form.append('name', config.name.trim())
        form.append('granularity', config.granularity)
        form.append('claim_rule', config.claimRule)
        form.append('custom_values', JSON.stringify(config.customValues))
        form.append('sent_by', config.sentBy)
        Loading.circle('Reading the spreadsheet...')
        try {
            const response = await apiUpload('contract-sign/bulk/upload-sheet', form)
            Loading.remove()
            if (!response.data?.success) {
                const extra = response.data?.missing_columns?.length
                    ? ` Missing: ${response.data.missing_columns.join(', ')}.`
                    : ''
                return Report.failure(
                    'Could not read the sheet',
                    (response.data?.message || 'Upload failed.') + extra,
                    'Okay',
                )
            }
            setResult(response.data as SheetUploadResult)
        } catch (e: any) {
            Loading.remove()
            Report.failure('Error', e?.response?.data?.message || 'Could not upload the spreadsheet.', 'Okay')
        }
    }

    const start = () => {
        if (!result) return
        confirmSend(result.total, result.delivery_type === 'esign', async () => {
            Loading.circle('Starting...')
            try {
                const response = await api({ url: 'contract-sign/bulk/start', data: { batch_id: result.batch_id } })
                Loading.remove()
                if (!response.data?.success) {
                    return Report.failure('Error', response.data?.message || 'Could not start the send.', 'Okay')
                }
                onBatchCreated(result.batch_id)
            } catch (e: any) {
                Loading.remove()
                Report.failure('Error', e?.response?.data?.message || 'Could not start the send.', 'Okay')
            }
        })
    }

    if (!config.template) {
        return (
            <div className={styles.emptyState}>Choose a template above to see the columns your spreadsheet needs.</div>
        )
    }

    if (result) {
        return (
            <>
                <div className={styles.basketSummary}>
                    <span className={styles.stackCell}>
                        <span className={styles.stackPrimary}>
                            {result.total} {result.total === 1 ? 'row' : 'rows'} ready · {result.skipped} skipped
                        </span>
                        <span className={styles.stackSecondary}>
                            Saved as a draft bulk send (#{result.batch_id}) — nothing has been sent yet.
                            {result.ignored_headers.length > 0 &&
                                ` Ignored columns: ${result.ignored_headers.join(', ')}.`}
                        </span>
                    </span>
                    <span className={styles.rowActions}>
                        <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => onBatchCreated(result.batch_id)}
                        >
                            Open draft
                        </button>
                        <button type="button" className={styles.secondaryBtn} onClick={() => setResult(null)}>
                            Upload a different file
                        </button>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            disabled={result.total === 0}
                            onClick={start}
                        >
                            Send {result.total} now
                        </button>
                    </span>
                </div>

                {result.preview.length > 0 && (
                    <div className={styles.panelContent}>
                        <p className={styles.miniHeading}>First rows</p>
                        <div className={styles.tableWrap}>
                            <table className={`${styles.table} ${styles.compactTable}`}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Case ref</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.preview.map((row, i) => (
                                        <tr key={i} className={styles.staticRow}>
                                            <td>{row.recipient_name || '—'}</td>
                                            <td>{row.recipient_email}</td>
                                            <td>{row.case_ref || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {result.skipped_rows.length > 0 && (
                    <div className={styles.panelContent}>
                        <p className={styles.miniHeading}>Skipped rows ({result.skipped})</p>
                        <div className={styles.tableWrap}>
                            <table className={`${styles.table} ${styles.compactTable}`}>
                                <thead>
                                    <tr>
                                        <th>Row</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.skipped_rows.map((row) => (
                                        <tr key={row.row} className={styles.staticRow}>
                                            <td className={styles.muted}>{row.row}</td>
                                            <td>{row.name || '—'}</td>
                                            <td>{row.email || '—'}</td>
                                            <td className={styles.toneBad}>{row.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </>
        )
    }

    return (
        <>
            <div className={styles.panelContent}>
                <p className={styles.miniHeading}>Columns for “{config.template.name}”</p>
                {columns === null ? (
                    <div className={styles.emptyState}>Loading columns...</div>
                ) : (
                    <div className={styles.chipRow}>
                        {columns.map((c) => (
                            <span
                                key={c.key}
                                className={`${styles.chip} ${c.required ? styles.chipGood : ''}`}
                                title={c.required ? 'Required' : 'Optional'}
                            >
                                {c.header}
                                {c.required ? ' *' : ''}
                            </span>
                        ))}
                    </div>
                )}
                <p className={styles.hint}>
                    * required. Headers are matched case-insensitively and common variants (e.g. “Full Name”,
                    “Telephone”, “DOB”) are accepted. Dates can be dd/mm/yyyy or real Excel dates. Custom text can come
                    from a column or from the values above.
                    {config.granularity === 'claim' && ' A per-claim send needs a Case Ref column.'}
                </p>
                <div className={styles.rowActions} style={{ marginTop: 10 }}>
                    <button
                        type="button"
                        className={styles.secondaryBtn}
                        disabled={!columns}
                        onClick={downloadTemplate}
                    >
                        <FaDownload /> Download header template
                    </button>
                </div>
            </div>

            <div className={styles.panelContent}>
                <div className={styles.formGrid}>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>Spreadsheet</label>
                        <label className={`${styles.filePicker} ${file ? styles.hasFile : ''}`}>
                            <FaUpload />
                            <span>{file ? file.name : 'Choose a .csv, .xlsx or .xls file'}</span>
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                        </label>
                    </div>
                </div>
                <div className={styles.panelActions}>
                    {file && (
                        <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => {
                                setFile(null)
                                if (inputRef.current) inputRef.current.value = ''
                            }}
                        >
                            Clear
                        </button>
                    )}
                    <button type="button" className={styles.primaryBtn} disabled={!file} onClick={upload}>
                        Upload and check rows
                    </button>
                </div>
            </div>
        </>
    )
}

/* Source: client search basket ------------------------------------------- */

interface BasketEntry {
    client: SelectedClient
    selectedClaims: Set<string>
}

const SearchSource = ({ config, onBatchCreated }: { config: BatchConfig; onBatchCreated: (id: number) => void }) => {
    const [results, setResults] = useState<SelectedClient[] | null>(null)
    const [basket, setBasket] = useState<Map<string, BasketEntry>>(new Map())
    const [openKey, setOpenKey] = useState<string | null>(null)

    const { needsClaim } = templateRequirements(config.fields)

    const addClient = (client: SelectedClient) => {
        const key = clientKey(client)
        setBasket((prev) => {
            const next = new Map(prev)
            if (next.has(key)) {
                next.delete(key)
                return next
            }
            const active = client.claims.filter((c) => !c.cancelled)
            next.set(key, {
                client,
                selectedClaims: new Set((active.length ? active : client.claims).map((c) => c.case_ref)),
            })
            return next
        })
    }

    const addAllResults = () => {
        if (!results) return
        setBasket((prev) => {
            const next = new Map(prev)
            results.forEach((client) => {
                const key = clientKey(client)
                if (next.has(key)) return
                const active = client.claims.filter((c) => !c.cancelled)
                next.set(key, {
                    client,
                    selectedClaims: new Set((active.length ? active : client.claims).map((c) => c.case_ref)),
                })
            })
            return next
        })
    }

    const remove = (key: string) =>
        setBasket((prev) => {
            const next = new Map(prev)
            next.delete(key)
            return next
        })

    const toggleClaim = (key: string, caseRef: string) =>
        setBasket((prev) => {
            const entry = prev.get(key)
            if (!entry) return prev
            const claims = new Set(entry.selectedClaims)
            if (claims.has(caseRef)) claims.delete(caseRef)
            else claims.add(caseRef)
            const next = new Map(prev)
            next.set(key, { ...entry, selectedClaims: claims })
            return next
        })

    const entries = Array.from(basket.entries())

    const items = useMemo<BatchItemInput[]>(() => {
        const out: BatchItemInput[] = []
        entries.forEach(([, entry]) => {
            const base = {
                client_id: entry.client.client_id,
                origin: entry.client.origin,
                recipient_name: entry.client.name,
                recipient_email: entry.client.email,
            }
            if (config.granularity === 'claim') {
                entry.client.claims
                    .filter((c) => entry.selectedClaims.has(c.case_ref))
                    .forEach((c) => out.push({ ...base, case_ref: c.case_ref }))
            } else {
                out.push({ ...base, case_ref: null })
            }
        })
        return out
    }, [entries, config.granularity])

    const warnings = useMemo(() => {
        let noEmail = 0
        let missingData = 0
        entries.forEach(([, entry]) => {
            if (!entry.client.email || !EMAIL_REGEX.test(entry.client.email)) noEmail += 1
            const claim = needsClaim ? pickClaimByRule(entry.client.claims, config.claimRule) : null
            if (missingForClient(config.fields, entry.client, claim).filter((m) => m !== 'email').length)
                missingData += 1
        })
        return { noEmail, missingData }
    }, [entries, config.fields, config.claimRule, needsClaim])

    const create = async (start: boolean) => {
        const id = await createBatch(config, 'search', items, { clients: entries.length }, start)
        if (id) onBatchCreated(id)
    }

    return (
        <>
            <ClientSearchForm onResults={setResults} />
            {results && results.length > 0 && (
                <div className={styles.rowActions} style={{ marginTop: 12 }}>
                    <button type="button" className={styles.secondaryBtn} onClick={addAllResults}>
                        Add all {results.length} results
                    </button>
                </div>
            )}
            <ClientResultList
                results={results}
                onPick={addClient}
                pickedKeys={new Set(basket.keys())}
                pickLabel="Add"
            />

            <div className={styles.basketSummary}>
                <span className={styles.stackCell}>
                    <span className={styles.stackPrimary}>
                        {entries.length} {entries.length === 1 ? 'client' : 'clients'} in this send
                        {config.granularity === 'claim' && ` · ${items.length} documents`}
                    </span>
                    <span className={styles.stackSecondary}>
                        {warnings.noEmail > 0 && `${warnings.noEmail} without a valid email (will fail). `}
                        {warnings.missingData > 0 && `${warnings.missingData} missing data the template stamps. `}
                        {warnings.noEmail === 0 &&
                            warnings.missingData === 0 &&
                            entries.length > 0 &&
                            'Everyone has the data this template needs.'}
                        {entries.length === 0 && 'Search above and add clients.'}
                    </span>
                </span>
                {entries.length > 0 && (
                    <button
                        type="button"
                        className={`${styles.secondaryBtn} ${styles.compactBtn}`}
                        onClick={() => setBasket(new Map())}
                    >
                        Clear list
                    </button>
                )}
            </div>

            {entries.length > 0 && (
                <div className={styles.tableWrap} style={{ marginTop: 12 }}>
                    <table className={`${styles.table} ${styles.compactTable}`}>
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Source</th>
                                <th>Claims</th>
                                <th>Data</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map(([key, entry]) => {
                                const claim = needsClaim ? pickClaimByRule(entry.client.claims, config.claimRule) : null
                                const missing = missingForClient(config.fields, entry.client, claim)
                                const open = openKey === key
                                return (
                                    <React.Fragment key={key}>
                                        <tr onClick={() => setOpenKey(open ? null : key)}>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>{entry.client.name}</span>
                                                    <span
                                                        className={`${styles.stackSecondary} ${entry.client.email ? '' : styles.toneBad}`}
                                                    >
                                                        {entry.client.email || 'no email'}
                                                        {entry.client.dob
                                                            ? ` · DOB ${formatDate(entry.client.dob)}`
                                                            : ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`${styles.pill} ${styles.muted}`}>
                                                    {entry.client.origin}
                                                </span>
                                            </td>
                                            <td className={styles.nowrap}>
                                                {config.granularity === 'claim'
                                                    ? `${entry.selectedClaims.size} of ${entry.client.claims.length} selected`
                                                    : `${entry.client.claims.length} on record`}
                                            </td>
                                            <td>
                                                {missing.length === 0 ? (
                                                    <span className={`${styles.chip} ${styles.chipGood}`}>
                                                        Complete
                                                    </span>
                                                ) : (
                                                    <span className={`${styles.chip} ${styles.chipBad}`}>
                                                        Missing {missing.map((m) => FIELD_TYPE_LABELS[m]).join(', ')}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={styles.compactBtn}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        remove(key)
                                                    }}
                                                    aria-label="Remove from list"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </td>
                                        </tr>
                                        {open && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan={5}>
                                                    <div className={styles.miniView}>
                                                        <p className={styles.miniHeading}>
                                                            Claims
                                                            {config.granularity === 'claim'
                                                                ? ' — tick each claim to send a document for'
                                                                : needsClaim
                                                                  ? ` — the ${config.claimRule} claim's details will be merged`
                                                                  : ''}
                                                        </p>
                                                        <ClaimsTable
                                                            claims={entry.client.claims}
                                                            mode={config.granularity === 'claim' ? 'checkbox' : 'none'}
                                                            selected={
                                                                config.granularity === 'claim'
                                                                    ? entry.selectedClaims
                                                                    : new Set(claim ? [claim.case_ref] : [])
                                                            }
                                                            onToggle={(caseRef) => toggleClaim(key, caseRef)}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <SendActions config={config} count={items.length} label="Client search list" onCreate={create} />
        </>
    )
}

/* Source: introducer filter ---------------------------------------------- */

interface FilterState {
    lender: string
    cfa_signed: 'any' | 'yes' | 'no'
    exclude_cancelled: boolean
    created_from: string
    created_to: string
    require_email: boolean
}

interface FilterPreview {
    total: number
    truncated: boolean
    items: ServerItem[]
    per_origin: { origin: string; clients: number; items: number }[]
    unsupported_origins: string[]
    errors: { origin: string; message: string }[]
}

const FilterSource = ({ config, onBatchCreated }: { config: BatchConfig; onBatchCreated: (id: number) => void }) => {
    const origins = useOrigins()
    const [selectedOrigins, setSelectedOrigins] = useState<Set<string>>(new Set())
    const [filters, setFilters] = useState<FilterState>({
        lender: '',
        cfa_signed: 'any',
        exclude_cancelled: true,
        created_from: '',
        created_to: '',
        require_email: true,
    })
    const [preview, setPreview] = useState<FilterPreview | null>(null)
    const [loading, setLoading] = useState(false)

    // A preview is only valid for the granularity it was built with.
    useEffect(() => {
        setPreview(null)
    }, [config.granularity, config.claimRule])

    const runPreview = async () => {
        if (selectedOrigins.size === 0)
            return Report.failure('Pick a source', 'Choose at least one data source.', 'Okay')
        setLoading(true)
        try {
            const response = await api({
                url: 'contract-sign/bulk/preview-filter',
                data: {
                    origins: Array.from(selectedOrigins),
                    filters,
                    granularity: config.granularity,
                    claim_rule: config.claimRule,
                },
            })
            if (!response.data?.success) {
                return Report.failure('Error', response.data?.message || 'Could not preview the filter.', 'Okay')
            }
            setPreview(response.data as FilterPreview)
        } catch (e: any) {
            Report.failure('Error', e?.response?.data?.message || 'Could not preview the filter.', 'Okay')
        } finally {
            setLoading(false)
        }
    }

    const create = async (start: boolean) => {
        if (!preview) return
        const id = await createBatch(
            config,
            'filter',
            preview.items.map(({ client_id, origin, case_ref, recipient_name, recipient_email }) => ({
                client_id,
                origin,
                case_ref,
                recipient_name,
                recipient_email,
            })),
            { origins: Array.from(selectedOrigins), filters },
            start,
        )
        if (id) onBatchCreated(id)
    }

    return (
        <>
            <div className={styles.panelContent}>
                <OriginsPicker origins={origins} selected={selectedOrigins} onChange={setSelectedOrigins} />
                <div className={styles.formGrid} style={{ marginTop: 14 }}>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>
                            Lender <span className={styles.fieldMeta}>contains</span>
                        </label>
                        <input
                            className={styles.input}
                            type="text"
                            value={filters.lender}
                            onChange={(e) => setFilters({ ...filters, lender: e.target.value })}
                            placeholder="Any lender"
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>CFA signed</label>
                        <select
                            className={styles.select}
                            value={filters.cfa_signed}
                            onChange={(e) =>
                                setFilters({ ...filters, cfa_signed: e.target.value as FilterState['cfa_signed'] })
                            }
                        >
                            <option value="any">Any</option>
                            <option value="yes">Signed only</option>
                            <option value="no">Unsigned only</option>
                        </select>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>Claims created from</label>
                        <input
                            className={styles.input}
                            type="date"
                            value={filters.created_from}
                            onChange={(e) => setFilters({ ...filters, created_from: e.target.value })}
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>Claims created to</label>
                        <input
                            className={styles.input}
                            type="date"
                            value={filters.created_to}
                            onChange={(e) => setFilters({ ...filters, created_to: e.target.value })}
                        />
                    </div>
                </div>
                <div className={styles.checkboxRow} style={{ marginTop: 14 }}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={filters.exclude_cancelled}
                            onChange={(e) => setFilters({ ...filters, exclude_cancelled: e.target.checked })}
                        />
                        Exclude cancelled claims
                    </label>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={filters.require_email}
                            onChange={(e) => setFilters({ ...filters, require_email: e.target.checked })}
                        />
                        Only clients with an email address
                    </label>
                </div>
                <div className={styles.panelActions}>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        disabled={loading || selectedOrigins.size === 0}
                        onClick={runPreview}
                    >
                        {loading ? 'Building population...' : 'Preview population'}
                    </button>
                </div>
            </div>

            {preview && (
                <div className={styles.panelContent}>
                    <div className={styles.basketSummary}>
                        <span className={styles.stackCell}>
                            <span className={styles.stackPrimary}>
                                {preview.total} {config.granularity === 'claim' ? 'documents' : 'clients'} matched
                                {preview.truncated && ' (showing the first part)'}
                            </span>
                            <span className={styles.stackSecondary}>
                                {preview.per_origin
                                    .map((p) => `${p.origin}: ${p.clients} clients / ${p.items} docs`)
                                    .join(' · ')}
                                {preview.errors.length > 0 &&
                                    ` · could not query: ${preview.errors.map((e) => e.origin).join(', ')}`}
                            </span>
                        </span>
                    </div>
                    <ItemsPreview items={preview.items} granularity={config.granularity} />
                </div>
            )}

            <SendActions
                config={config}
                count={preview?.items.length || 0}
                label="Introducer filter"
                disabledReason={preview ? null : 'Preview the population first.'}
                onCreate={create}
            />
        </>
    )
}

/* Source: identifier list ------------------------------------------------ */

interface ResolveResult {
    total: number
    items: ServerItem[]
    matched: number
    unmatched: string[]
    errors: { origin: string; message: string }[]
}

const parseIdentifiers = (text: string) =>
    Array.from(
        new Set(
            text
                .split(/[\s,;]+/)
                .map((s) => s.trim().replace(/^["']|["']$/g, ''))
                .filter(Boolean),
        ),
    )

const IdentifierSource = ({
    config,
    onBatchCreated,
}: {
    config: BatchConfig
    onBatchCreated: (id: number) => void
}) => {
    const origins = useOrigins()
    const [selectedOrigins, setSelectedOrigins] = useState<Set<string>>(new Set())
    const [text, setText] = useState('')
    const [result, setResult] = useState<ResolveResult | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setResult(null)
    }, [config.granularity, config.claimRule])

    // Default to every filterable source once they arrive.
    useEffect(() => {
        if (origins.length && selectedOrigins.size === 0) {
            setSelectedOrigins(new Set(origins.filter((o) => o.supports_filter).map((o) => o.key)))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origins])

    const identifiers = useMemo(() => parseIdentifiers(text), [text])

    const readFile = async (file: File | null) => {
        if (!file) return
        const content = await file.text()
        // Take the first column of a CSV, or every token of a plain list.
        const firstColumn = content
            .split(/\r?\n/)
            .map((line) => line.split(',')[0]?.trim() || '')
            .filter(Boolean)
        const looksLikeHeader =
            firstColumn[0] && !EMAIL_REGEX.test(firstColumn[0]) && /email|case|ref/i.test(firstColumn[0])
        setText((prev) => [prev, ...(looksLikeHeader ? firstColumn.slice(1) : firstColumn)].filter(Boolean).join('\n'))
    }

    const resolve = async () => {
        if (selectedOrigins.size === 0)
            return Report.failure('Pick a source', 'Choose at least one data source.', 'Okay')
        if (identifiers.length === 0)
            return Report.failure('Nothing to look up', 'Paste some emails or case references first.', 'Okay')
        setLoading(true)
        try {
            const response = await api({
                url: 'contract-sign/bulk/resolve-identifiers',
                data: {
                    origins: Array.from(selectedOrigins),
                    identifiers,
                    granularity: config.granularity,
                    claim_rule: config.claimRule,
                },
            })
            if (!response.data?.success) {
                return Report.failure('Error', response.data?.message || 'Could not resolve the identifiers.', 'Okay')
            }
            setResult(response.data as ResolveResult)
        } catch (e: any) {
            Report.failure('Error', e?.response?.data?.message || 'Could not resolve the identifiers.', 'Okay')
        } finally {
            setLoading(false)
        }
    }

    const create = async (start: boolean) => {
        if (!result) return
        const id = await createBatch(
            config,
            'identifiers',
            result.items.map(({ client_id, origin, case_ref, recipient_name, recipient_email }) => ({
                client_id,
                origin,
                case_ref,
                recipient_name,
                recipient_email,
            })),
            { origins: Array.from(selectedOrigins), identifiers: identifiers.length, unmatched: result.unmatched },
            start,
        )
        if (id) onBatchCreated(id)
    }

    // Identifier items always carry the granularity they were resolved with; a
    // case ref pins a claim regardless, so per-client sends may still include
    // pinned claims — that's intended.
    const previewGranularity: Granularity = result?.items.some((i) => i.case_ref) ? 'claim' : config.granularity

    return (
        <>
            <div className={styles.panelContent}>
                <OriginsPicker origins={origins} selected={selectedOrigins} onChange={setSelectedOrigins} />
                <div className={styles.field} style={{ marginTop: 14 }}>
                    <label className={styles.fieldLabel}>
                        Emails or case references
                        <span className={styles.fieldMeta}>
                            one per line or comma separated · {identifiers.length} found
                        </span>
                    </label>
                    <textarea
                        className={styles.textarea}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={'client@example.com\npcpSNT12345\n…'}
                    />
                </div>
                <div className={styles.rowActions} style={{ marginTop: 10 }}>
                    <label className={styles.filePicker} style={{ width: 'auto' }}>
                        <FaUpload />
                        <span>Add from .csv / .txt</span>
                        <input
                            type="file"
                            accept=".csv,.txt,text/plain,text/csv"
                            onChange={(e) => readFile(e.target.files?.[0] || null)}
                        />
                    </label>
                    {text && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => setText('')}>
                            Clear
                        </button>
                    )}
                </div>
                <p className={styles.hint}>
                    A case reference pins the document to that claim. An email finds the client and follows the
                    per-client / per-claim setting above. Identifiers found in several introducers are included for
                    each.
                </p>
                <div className={styles.panelActions}>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        disabled={loading || identifiers.length === 0 || selectedOrigins.size === 0}
                        onClick={resolve}
                    >
                        {loading ? 'Looking up...' : `Look up ${identifiers.length || ''} identifiers`}
                    </button>
                </div>
            </div>

            {result && (
                <div className={styles.panelContent}>
                    <div className={styles.basketSummary}>
                        <span className={styles.stackCell}>
                            <span className={styles.stackPrimary}>
                                {result.matched} of {identifiers.length} identifiers matched · {result.total} documents
                            </span>
                            <span className={styles.stackSecondary}>
                                {result.unmatched.length > 0
                                    ? `Not found: ${result.unmatched.slice(0, 12).join(', ')}${result.unmatched.length > 12 ? ` and ${result.unmatched.length - 12} more` : ''}`
                                    : 'Every identifier was found.'}
                                {result.errors.length > 0 &&
                                    ` · could not query: ${result.errors.map((e) => e.origin).join(', ')}`}
                            </span>
                        </span>
                        {result.unmatched.length > 0 && (
                            <button
                                type="button"
                                className={`${styles.secondaryBtn} ${styles.compactBtn}`}
                                onClick={() =>
                                    downloadText(
                                        'unmatched-identifiers.csv',
                                        `Identifier\n${result.unmatched.join('\n')}\n`,
                                    )
                                }
                            >
                                <FaDownload /> Unmatched list
                            </button>
                        )}
                    </div>
                    <ItemsPreview items={result.items} granularity={previewGranularity} />
                </div>
            )}

            <SendActions
                config={config}
                count={result?.items.length || 0}
                label="Identifier list"
                disabledReason={result ? null : 'Look up the identifiers first.'}
                onCreate={create}
            />
        </>
    )
}

/* Tab ----------------------------------------------------------------------- */

export const BulkSendTab = ({
    templates,
    templatesLoaded,
    onBatchCreated,
    onGoToVault,
    onEditTemplate,
}: BulkSendTabProps) => {
    const authUser = useAuthUserData()
    const sendable = useMemo(() => templates.filter((t) => t.fields.length > 0), [templates])

    const [templateId, setTemplateId] = useState<number | null>(null)
    const [granularity, setGranularity] = useState<Granularity>('client')
    const [granularityTouched, setGranularityTouched] = useState(false)
    const [claimRule, setClaimRule] = useState<ClaimRule>('latest')
    const [customValues, setCustomValues] = useState<Record<string, string>>({})
    const [name, setName] = useState('')
    const [source, setSource] = useState<SourceKind>('sheet')

    const template = useMemo(() => sendable.find((t) => t.id === templateId) || null, [sendable, templateId])
    const fields = useMemo(() => template?.fields || [], [template])
    const requirements = useMemo(() => templateRequirements(fields), [fields])
    const templateDefault = useMemo(() => defaultGranularity(fields), [fields])

    useEffect(() => {
        setCustomValues({})
        setGranularityTouched(false)
    }, [templateId])

    useEffect(() => {
        if (!granularityTouched) setGranularity(templateDefault)
    }, [templateDefault, granularityTouched])

    const config: BatchConfig = {
        template,
        fields,
        granularity,
        claimRule,
        customValues,
        name,
        sentBy: authUser?.email ?? '',
    }

    return (
        <>
            <section className={styles.panel}>
                <div className={styles.panelHeader}>
                    <div className={styles.panelHeaderText}>
                        <h4 className={styles.panelTitle}>
                            <span className={styles.stepBadge}>1</span> Template and options
                        </h4>
                        <p className={styles.panelBody}>
                            The template decides which details each document needs. Choose whether to produce one
                            document per client or one per claim, then fill in any custom text once for the whole send.
                        </p>
                    </div>
                    <button type="button" className={styles.secondaryBtn} onClick={onGoToVault}>
                        Contract templates
                    </button>
                </div>

                <TemplatePicker
                    templates={templates}
                    loaded={templatesLoaded}
                    value={templateId}
                    onChange={setTemplateId}
                    onNeedsFields={onEditTemplate}
                />

                {template && (
                    <>
                        <GranularityControl
                            value={granularity}
                            onChange={(g) => {
                                setGranularityTouched(true)
                                setGranularity(g)
                            }}
                            defaultValue={templateDefault}
                            claimRule={claimRule}
                            onClaimRuleChange={setClaimRule}
                            needsClaim={requirements.needsClaim}
                        />
                        <div className={styles.formGrid} style={{ marginTop: 14 }}>
                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>
                                    Bulk send name <span className={styles.fieldMeta}>optional</span>
                                </label>
                                <input
                                    className={styles.input}
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={`${template.name} — ${new Date().toLocaleDateString('en-GB')}`}
                                />
                            </div>
                        </div>
                        {requirements.customFields.length > 0 && (
                            <div style={{ marginTop: 14 }}>
                                <p className={styles.miniHeading}>Custom text (same on every document)</p>
                                <CustomValuesForm fields={fields} values={customValues} onChange={setCustomValues} />
                            </div>
                        )}
                        <div className={`${styles.note} ${requirements.hasSignature ? styles.accent : ''}`}>
                            <FaInfoCircle />
                            <span>
                                {requirements.hasSignature
                                    ? 'This template has a signature box: every recipient gets their own signing link and you can track who has signed under Bulk sends.'
                                    : 'This template has no signature box: each recipient receives the populated PDF as an attachment.'}
                                {requirements.needsClaim &&
                                    (granularity === 'claim'
                                        ? ' One document is produced for each claim.'
                                        : ` Claim details come from each client's ${claimRule} claim.`)}
                            </span>
                        </div>
                    </>
                )}
            </section>

            <section className={styles.panel}>
                <div className={styles.panelHeader}>
                    <div className={styles.panelHeaderText}>
                        <h4 className={styles.panelTitle}>
                            <span className={styles.stepBadge}>2</span> Who receives it
                        </h4>
                        <p className={styles.panelBody}>Build the recipient list from any of these sources.</p>
                    </div>
                </div>

                <div className={styles.sourceTabs} role="tablist" aria-label="Recipient source">
                    {SOURCES.map((s) => (
                        <button
                            key={s.kind}
                            type="button"
                            role="tab"
                            aria-selected={source === s.kind}
                            className={`${styles.sourceTab} ${source === s.kind ? styles.active : ''}`}
                            onClick={() => setSource(s.kind)}
                        >
                            <span className={styles.sourceTabTitle}>
                                {s.icon} {s.title}
                            </span>
                            <span className={styles.sourceTabBody}>{s.body}</span>
                        </button>
                    ))}
                </div>

                {!template ? (
                    <div className={styles.emptyState}>Choose a template first.</div>
                ) : (
                    <>
                        {source === 'sheet' && (
                            <SheetSource key={template.id} config={config} onBatchCreated={onBatchCreated} />
                        )}
                        {source === 'search' && <SearchSource config={config} onBatchCreated={onBatchCreated} />}
                        {source === 'filter' && <FilterSource config={config} onBatchCreated={onBatchCreated} />}
                        {source === 'identifiers' && (
                            <IdentifierSource config={config} onBatchCreated={onBatchCreated} />
                        )}
                    </>
                )}
            </section>
        </>
    )
}
